import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { downloadsService } from "client-app-core";
import PropTypes from "prop-types";

export const sortDownloads = downloads => {
	return [...downloads].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
};

export const reconcileDownloadChange = (downloads, change) => {
	const changedDownload = change.new_val || change.old_val;
	if (!changedDownload?.id) return downloads;

	if (change.type === "remove" || !change.new_val || change.new_val.deletedDate) {
		return sortDownloads(downloads.filter(download => download.id !== changedDownload.id));
	}

	const nextDownloads = downloads.filter(download => download.id !== changedDownload.id);
	nextDownloads.push(change.new_val);
	return sortDownloads(nextDownloads);
};

const DownloadsContext = createContext(null);

export const DownloadsProvider = ({ children, enabled = true }) => {
	const [downloads, setDownloads] = useState([]);
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState(null);
	const mounted = useRef(true);
	const refreshGeneration = useRef(0);
	const streamSnapshot = useRef([]);
	const streamInitializing = useRef(false);
	const subscriptionRef = useRef(null);
	const subscriptionGeneration = useRef(0);
	const activeSubscriptionGeneration = useRef(0);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const refreshDownloads = useCallback((options = {}) => {
		if (!enabled || !mounted.current) return;

		const { showLoading = false } = options;
		if (showLoading) setLoading(true);
		setError(null);
		const generation = ++refreshGeneration.current;
		downloadsService.getMyDownloads((err, response) => {
			if (!mounted.current || generation !== refreshGeneration.current) return;

			if (err) {
				setError(err);
			} else {
				setDownloads(sortDownloads(Array.isArray(response) ? response : []));
			}
			setLoading(false);
		});
	}, [enabled]);

	useEffect(() => {
		if (!enabled) {
			refreshGeneration.current++;
			setDownloads([]);
			setLoading(false);
			setError(null);
			return undefined;
		}

		let active = true;
		const generation = ++subscriptionGeneration.current;
		activeSubscriptionGeneration.current = generation;
		refreshDownloads({ showLoading: true });

		const subscriptionPromise = downloadsService.streamMyDownloads((err, change) => {
			if (!active || !mounted.current) return;
			if (err) {
				refreshDownloads();
				return;
			}
			if (!change) return;

			if (change.state === "initializing") {
				streamInitializing.current = true;
				streamSnapshot.current = [];
				return;
			}

			if (change.state === "ready") {
				if (streamInitializing.current) {
					refreshGeneration.current++;
					setDownloads(sortDownloads(streamSnapshot.current));
					setError(null);
					setLoading(false);
				}
				streamInitializing.current = false;
				streamSnapshot.current = [];
				return;
			}

			if (streamInitializing.current) {
				refreshGeneration.current++;
				streamSnapshot.current = reconcileDownloadChange(streamSnapshot.current, change);
			} else {
				refreshGeneration.current++;
				setDownloads(currentDownloads => reconcileDownloadChange(currentDownloads, change));
				setError(null);
				setLoading(false);
			}
		});

		Promise.resolve(subscriptionPromise)
			.then(result => {
				if (result?.channel === "shared") return;

				if (active || activeSubscriptionGeneration.current) {
					subscriptionRef.current = result;
				} else {
					result?.unsubscribe?.();
				}
			})
			.catch(() => {
				if (active) refreshDownloads();
			});

		return () => {
			active = false;
			if (activeSubscriptionGeneration.current === generation) {
				activeSubscriptionGeneration.current = 0;
			}
			subscriptionRef.current?.unsubscribe?.();
			subscriptionRef.current = null;
			streamInitializing.current = false;
			streamSnapshot.current = [];
		};
	}, [enabled, refreshDownloads]);

	useEffect(() => {
		if (!enabled) return undefined;

		const handleExportStarted = () => refreshDownloads();
		window.addEventListener("downloads-manager-export-started", handleExportStarted);
		return () => {
			window.removeEventListener("downloads-manager-export-started", handleExportStarted);
		};
	}, [enabled, refreshDownloads]);

	const value = useMemo(
		() => ({
			downloads,
			downloadsCount: downloads.length,
			loading,
			error,
			refreshDownloads
		}),
		[downloads, loading, error, refreshDownloads]
	);

	return <DownloadsContext.Provider value={value}>{children}</DownloadsContext.Provider>;
};

export const useDownloads = () => {
	const context = useContext(DownloadsContext);
	if (!context) {
		throw new Error("useDownloads must be used within a DownloadsProvider");
	}
	return context;
};

DownloadsProvider.propTypes = {
	children: PropTypes.node.isRequired,
	enabled: PropTypes.bool
};
